import { useState, useEffect, useMemo } from 'react'
import { View, Text, StyleSheet } from 'react-native'
import { useTranslation } from 'react-i18next'
import { supabase } from '../../lib/supabase'
import { colors, fonts } from '../../lib/theme'
import { useThemeColors } from '../../lib/useThemeColors'
import { Card, SubHeader, RowBtn } from '../../components/Leagues/ui'
import Ionicons from '@expo/vector-icons/Ionicons'
import Sheet from './Sheet'

export default function SezioneStatistiche({ leagueId }) {
  const { t } = useTranslation()
  const tc = useThemeColors()
  const styles = useMemo(() => makeStyles(tc), [tc])
  const [open, setOpen]               = useState(false)
  const [stats, setStats]             = useState([])
  const [loading, setLoading]         = useState(false)
  const [error, setError]       = useState(false)

  useEffect(() => {
    if (!open) return
    setLoading(true); setError(false)
    supabase.rpc('fn_league_participation', { p_id_league: leagueId }).then(({ data, error: err }) => {
      if (err) { setError(true); setStats([]) }
      else setStats((data ?? []).map(r => ({
        id_stage: r.id_stage,
        // nei giri a tappe "Giro T5", nelle gare di un giorno solo il nome
        label: r.id_type_race === 1 || r.id_type_race === 2 ? `${r.race_name} T${r.stage_number}` : r.race_name,
        count: r.participants, total: r.members,
        pct: r.members > 0 ? Math.round(r.participants / r.members * 100) : 0,
      })))
      setLoading(false)
    })
  }, [open, leagueId])

  return (
    <>
      <Card>
        <View style={styles.cardRow}>
          <View style={{ flex: 1 }}>
            <SubHeader icon={<Ionicons name="bar-chart-outline" size={16} color={tc.rosaText} />}>{t('league.stats_section')}</SubHeader>
            <Text style={[styles.cardDesc, { color: tc.textMuted }]}>{t('league.stats_participation')}</Text>
          </View>
          <RowBtn onPress={() => setOpen(true)}>{t('league.view')}</RowBtn>
        </View>
      </Card>
      <Sheet visible={open} title={t('league.stats_title')} onClose={() => setOpen(false)}>
        {loading
          ? <Text style={{ color: tc.textMuted, fontSize: 13, textAlign: 'center', padding: 20 }}>{t('league.loading')}</Text>
          : error
            ? <Text style={{ color: tc.rosaText, fontSize: 13, textAlign: 'center', padding: 20 }} accessibilityRole="alert">{t('league.stats_error')}</Text>
          : stats.length === 0
            ? <Text style={{ color: tc.textMuted, fontSize: 13, textAlign: 'center', padding: 20 }}>{t('league.stats_no_data')}</Text>
            : (
              <View style={{ gap: 10 }}>
                {stats.map(s => (
                  <View key={s.id_stage} style={[styles.statRow, { backgroundColor: tc.bgCard2, borderColor: tc.border }]}>
                    <View style={styles.statHeader}>
                      <Text style={[styles.statLabel, { color: tc.text }]}>{s.label}</Text>
                      <Text style={[styles.statPct, { color: s.pct === 100 ? tc.success : s.pct >= 50 ? tc.rosaText : tc.warning }]}>
                        {s.count}/{s.total} ({s.pct}%)
                      </Text>
                    </View>
                    <View style={[styles.statTrack, { backgroundColor: tc.border }]}>
                      <View style={[styles.statFill, { width: `${s.pct}%`, backgroundColor: s.pct === 100 ? tc.success : s.pct >= 50 ? tc.rosa : tc.warning }]} />
                    </View>
                  </View>
                ))}
              </View>
            )}
      </Sheet>
    </>
  )
}

const makeStyles = (tc) => StyleSheet.create({
  cardRow:    { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 10 },
  cardDesc:   { fontSize: 12, marginTop: -8 },
  statRow:    { borderRadius: 10, padding: 10, paddingHorizontal: 14, borderWidth: 1 },
  statHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 },
  statLabel:  { fontSize: 13, fontWeight: '600' },
  statPct:    { fontSize: 13, fontFamily: fonts.condensedBold },
  statTrack:  { height: 6, borderRadius: 4, overflow: 'hidden' },
  statFill:   { height: '100%', borderRadius: 4 },
})